import React, { useState } from 'react';
import { useRegisterTopEscapeClose } from '../hooks/useEscapeCloseStack';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Copy, Layers } from 'lucide-react';

function DuplicatePropertyModal({ property, onClose, onConfirm }) {
  const { t } = useLanguage();
  const [duplicating, setDuplicating] = useState(false);

  const fullAddress = [
    property?.address,
    property?.city,
    property?.province,
    property?.postalCode,
  ]
    .filter(Boolean)
    .join(', ') || t('home.address.unset');

  const handleChoice = async (includeScenarios) => {
    setDuplicating(true);
    try {
      await Promise.resolve(onConfirm?.(includeScenarios));
    } finally {
      setDuplicating(false);
    }
  };

  useRegisterTopEscapeClose(onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{t('duplicateProperty.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={duplicating}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
            aria-label={t('cancel')}
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-gray-600 text-sm mb-1">{t('duplicateProperty.description')}</p>
        <p className="text-gray-900 font-medium text-sm mb-4 truncate" title={fullAddress}>
          {fullAddress}
        </p>
        <p className="text-gray-500 text-xs mb-4">{t('duplicateProperty.noSharingHint')}</p>
        <div className="space-y-2">
          <button
            type="button"
            disabled={duplicating}
            onClick={() => handleChoice(false)}
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50/50 text-left transition-colors disabled:opacity-50"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Copy className="text-gray-600" size={20} />
            </div>
            <div>
              <span className="font-medium text-gray-900">{t('duplicateProperty.withoutScenarios')}</span>
              <p className="text-sm text-gray-500">{t('duplicateProperty.withoutScenariosHint')}</p>
            </div>
          </button>
          <button
            type="button"
            disabled={duplicating}
            onClick={() => handleChoice(true)}
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50/50 text-left transition-colors disabled:opacity-50"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Layers className="text-blue-600" size={20} />
            </div>
            <div>
              <span className="font-medium text-gray-900">{t('duplicateProperty.withScenarios')}</span>
              <p className="text-sm text-gray-500">{t('duplicateProperty.withScenariosHint')}</p>
            </div>
          </button>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={duplicating}
            className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DuplicatePropertyModal;
